(function (global) {
  'use strict';

  function safeId(value) {
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : 0;
  }

  function quantityValue(value) {
    const number = Number(value);
    return Math.max(1, Math.min(99, Math.trunc(number) || 1));
  }

  function canPurchase(variant) {
    return Boolean(variant && variant.available === true && variant.depositAvailable === true && safeId(variant.id) && safeId(variant.depositId) && [6, 12, 18, 24].includes(variant.bottles));
  }

  function buildCartItems(variant, quantity) {
    if (!canPurchase(variant)) throw new Error('Dieses Gebinde ist derzeit nicht bestellbar. Bitte wähle eine andere verfügbare Packungsgröße.');
    const amount = quantityValue(quantity);
    return [
      { id: safeId(variant.depositId), quantity: amount, properties: { _pfand: 'true', _pfand_for_variant_id: String(variant.id) } },
      { id: safeId(variant.id), quantity: amount, properties: { _pfand_variant_id: String(variant.depositId) } }
    ];
  }

  function unitPriceCents(variant, bottleMl) {
    const volume = variant.bottles * Number(bottleMl);
    if (!Number.isFinite(volume) || volume <= 0) return null;
    return Math.round(variant.price * 1000 / volume);
  }

  const utilities = Object.freeze({ safeId, quantityValue, canPurchase, buildCartItems, unitPriceCents });
  if (typeof module === 'object' && module.exports) module.exports = utilities;
  if (!global.document || !global.customElements || global.customElements.get('bs-product-page')) return;

  class BummsschorleProduct extends global.HTMLElement {
    connectedCallback() {
      if (this.bsInitialised) return;
      const data = this.querySelector('[data-bs-variants]');
      if (!data) {
        if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this.bsInitialised = true;
      this.abortController = new global.AbortController();
      this.refreshFormControls();
      this.message = this.querySelector('[data-bs-message]');
      this.mobilebar = this.querySelector('[data-bs-mobilebar]');
      this.zoomDialog = this.querySelector('[data-bs-zoom-dialog]');
      this.busy = false;
      try {
        this.variants = JSON.parse(data.textContent);
        if (!Array.isArray(this.variants)) throw new Error('Ungültige Produktdaten.');
      } catch (_) {
        this.showMessage('Die Produktauswahl konnte nicht geladen werden. Bitte lade die Seite neu.');
        return;
      }
      const initialId = safeId(this.dataset.initialVariant);
      this.selectedVariant = this.variants.find(variant => safeId(variant.id) === initialId) || this.variants[0];
      this.currency = this.dataset.currency || 'EUR';
      this.bottleMl = Number(this.dataset.bottleMl) || 330;

      // VO Product Options clones the submit button and adds its own click
      // handler. Capture clicks on the stable section before that handler can
      // submit the app's previous variant. Keep all other app controls intact.
      this.on(this, 'click', event => {
        const button = event.target.closest('[data-bs-submit]');
        if (!button || button.closest('bs-product-page') !== this) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!button.disabled && !this.busy) this.addToCart(event);
      }, { capture: true });

      this.on(this, 'change', event => {
        if (event.target.matches('[data-bs-package]') && !this.busy) {
          this.selectedVariant = this.variants.find(variant => safeId(variant.id) === safeId(event.target.value));
          this.hideMessage();
          this.updateProduct(true);
        }
        if (event.target === this.quantityInput) this.updateQuantity();
      });
      this.on(this, 'click', event => {
        const stepper = event.target.closest('[data-bs-quantity]');
        if (stepper && !this.busy) {
          const step = stepper.dataset.bsQuantity === 'plus' ? 1 : -1;
          this.quantityInput.value = String(quantityValue(this.quantityInput.value) + step);
          this.updateQuantity();
        }
        if (event.target.closest('[data-bs-scroll-purchase]')) {
          this.querySelector('.bs-p-purchase').scrollIntoView({ behavior: global.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        }
        if (event.target.closest('[data-bs-open-details]')) this.querySelector('[data-bs-ingredients]').open = true;
        if (event.target.closest('[data-bs-zoom-open]') && this.zoomDialog && typeof this.zoomDialog.showModal === 'function') this.zoomDialog.showModal();
        if (event.target.closest('[data-bs-zoom-close]') && this.zoomDialog) this.zoomDialog.close();
        if (event.target === this.zoomDialog) {
          const rect = this.zoomDialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.zoomDialog.close();
        }
      });
      this.on(this, 'submit', event => {
        if (event.target.matches('.bs-p-form')) this.addToCart(event);
      }, { capture: true });
      this.on(this.quantityInput, 'blur', () => this.updateQuantity());
      this.on(global, 'scroll', () => this.scheduleMobilebar(), { passive: true });
      this.on(global, 'resize', () => this.scheduleMobilebar());
      if (this.zoomDialog) this.on(this.zoomDialog, 'close', () => this.updateMobilebar());
      this.updateQuantity();
      this.updateProduct(false);
      this.updateMobilebar();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.bsInitialised = false;
    }

    on(target, event, handler, options) {
      target.addEventListener(event, handler, Object.assign({}, options, { signal: this.abortController.signal }));
    }

    refreshFormControls() {
      this.form = this.querySelector('.bs-p-form');
      this.variantInput = this.querySelector('[data-bs-variant-input]');
      this.depositInput = this.querySelector('[data-bs-deposit-input]');
      this.quantityInput = this.querySelector('[data-bs-quantity-input]');
      this.submitButton = this.querySelector('[data-bs-submit]');
      this.submitLabel = this.querySelector('[data-bs-submit-label]');
    }

    money(cents) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.currency }).format(cents / 100);
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = false;
    }

    hideMessage() {
      this.message.hidden = true;
      this.message.textContent = '';
    }

    updateQuantity() {
      const quantity = quantityValue(this.quantityInput.value);
      this.quantityInput.value = String(quantity);
      this.querySelector('[data-bs-quantity="minus"]').disabled = this.busy || quantity <= 1;
      this.querySelector('[data-bs-quantity="plus"]').disabled = this.busy || quantity >= 99;
    }

    updateProduct(updateUrl) {
      // An app may have replaced the button since the last variant change.
      this.refreshFormControls();
      const variant = this.selectedVariant;
      if (!variant) {
        this.submitButton.disabled = true;
        this.showMessage('Dieses Produkt hat zurzeit keine bestellbare Variante.');
        return;
      }
      this.variantInput.value = String(variant.id);
      this.depositInput.value = variant.depositId ? String(variant.depositId) : '';
      this.querySelector('[data-bs-price]').textContent = this.money(variant.price);
      this.querySelector('[data-bs-deposit-line]').hidden = !variant.depositId;
      this.querySelector('[data-bs-deposit-price]').textContent = this.money(variant.depositPrice);
      const unitCents = unitPriceCents(variant, this.bottleMl);
      this.querySelector('[data-bs-unit-price]').textContent = unitCents === null ? '' : `${this.money(unitCents)}/l`;
      this.querySelector('[data-bs-mobile-package]').textContent = variant.bottles ? `${variant.bottles} Flaschen` : variant.title;
      this.querySelector('[data-bs-mobile-price]').textContent = this.money(variant.price);
      this.querySelector('[data-bs-mobile-deposit]').textContent = variant.depositId ? `zzgl. ${this.money(variant.depositPrice)} Pfand` : '';
      this.submitButton.disabled = this.busy || !canPurchase(variant);
      this.submitLabel.textContent = canPurchase(variant) ? 'In den Warenkorb' : 'Zurzeit nicht bestellbar';
      this.querySelectorAll('[data-bs-package]').forEach(radio => { radio.checked = safeId(radio.value) === safeId(variant.id); });
      if (updateUrl && !global.Shopify?.designMode) {
        const url = new URL(global.location.href);
        url.searchParams.set('variant', String(variant.id));
        global.history.replaceState({}, '', url);
      }
      this.scheduleMobilebar();
    }

    async addToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (this.busy) return;
      let items;
      try {
        this.refreshFormControls();
        const selectedPackage = Array.from(this.querySelectorAll('[data-bs-package]')).find(radio => radio.checked);
        this.selectedVariant = this.variants.find(variant => safeId(variant.id) === safeId(selectedPackage?.value));
        if (!this.selectedVariant) throw new Error('Bitte wähle ein verfügbares Gebinde aus.');
        this.updateProduct(false);
        this.updateQuantity();
        items = buildCartItems(this.selectedVariant, this.quantityInput.value);
      } catch (error) {
        this.showMessage(error.message);
        return;
      }
      this.hideMessage();
      this.busy = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitLabel.textContent = 'Wird hinzugefügt …';
      this.quantityInput.disabled = true;
      this.updateQuantity();
      this.querySelectorAll('[data-bs-package]').forEach(radio => { radio.disabled = true; });
      try {
        const response = await global.fetch(this.dataset.cartAddUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok || result.status >= 400) {
          const error = new Error('Cart request rejected');
          error.customerMessage = typeof result.description === 'string' ? result.description : 'Das Gebinde konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
          throw error;
        }
        global.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.busy = false;
        this.quantityInput.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
        this.querySelectorAll('[data-bs-package]').forEach(radio => {
          const variant = this.variants.find(item => safeId(item.id) === safeId(radio.value));
          radio.disabled = !canPurchase(variant);
        });
        this.updateQuantity();
        this.updateProduct(false);
        this.showMessage(error.customerMessage || 'Die Verbindung zum Warenkorb ist gerade unterbrochen. Bitte versuche es erneut.');
      }
    }

    scheduleMobilebar() {
      if (this.framePending) return;
      this.framePending = true;
      global.requestAnimationFrame(() => { this.updateMobilebar(); this.framePending = false; });
    }

    updateMobilebar() {
      const rect = this.submitButton.getBoundingClientRect();
      const visible = rect.top < global.innerHeight && rect.bottom > 0;
      this.mobilebar.hidden = global.scrollY < 250 || visible || Boolean(this.zoomDialog?.open) || !canPurchase(this.selectedVariant);
    }
  }

  global.customElements.define('bs-product-page', BummsschorleProduct);
})(typeof window === 'undefined' ? globalThis : window);
